import { useContext, useState } from 'react';

import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useUpdateEffect } from 'usehooks-ts';

import { BUSINESS_PROV_SELECTOR_FIELDNAME } from '../components/business/checkout/CheckoutBusinessProvSelect';
import { CLIENT_SELECTOR_FIELDNAME } from '../components/business/checkout/CheckoutClientSelect';
import { DEPOSIT_FIELDNAME } from '../components/business/checkout/CheckoutDeposit';
import { SEND_BOOKING_MAIL_FIELDNAME } from '../components/business/checkout/CheckoutMailToggle';
import { MERGE_ORDER_FIELDNAME } from '../components/business/checkout/CheckoutMergeOrderCheckbox';
import { PAYMENTLINK_TEMPLATE_FIELDNAME } from '../components/business/checkout/CheckoutPaymentLinkTemplate';
import { PAYMENT_METHOD_FIELDNAME } from '../components/business/checkout/CheckoutPaymentMethod';
import { CHECKOUT_EXTRA_DATA_FIELDNAME } from '../components/business/checkout/form/CheckoutFieldsExtraData';
import { EMAIL_FIELDNAME } from '../components/generic/form/InputEmail';
import { PHONE_COUNTRYCODE_FIELDNAME, PHONE_FIELDNAME } from '../components/generic/form/InputPhone';
import { RADIO_YES } from '../components/generic/form/InputRadioCard';
import { apiCheckout, LOCAL_STORAGE_CART_ID, ROUTE_PAGE_CHECKOUT_SUCCESS } from '../constants';
import { CoreContext } from '../context/CoreContext';
import { gtmPushBeginCheckout } from '../gtm';
import { adaptPhoneForBackend } from '../utils/phoneUtil';
import { REQUEST_STATUS } from '../utils/requestUtil';
import { isStrNullOrEmpty, trimObjectKeys } from '../utils/strUtil';
import useCleanStorage from './useCleanStorage';
import useNavigateBookingEngine from './useNavigateBookingEngine';
import usePost from './usePost';
import { FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME } from './useReadAdditionalData';
import useReadCartMissingOrder from './useReadCartMissingOrder';
import { CART_SOURCE_LOGGED_OFFICE } from './useReadCartSource';
import useReadLoggedUser from './useReadLoggedUser';
import useReadManufacturer from './useReadManufacturer';
import { NO_PAYMENT_METHOD, ONLINE_PAYMENT_METHOD, PAYMENT_LINK_METHOD, PAYMENT_TERMINAL_METHOD } from './useReadPaymentMethods';
import useReadStorageBookingEngine from './useReadStorageBookingEngine';

const usePostCheckout = (cartMainClientId, currency, totalPrice, onStripeOrderCreated) => {
  const storedCartId = useReadStorageBookingEngine(LOCAL_STORAGE_CART_ID);

  // This is for the save_order=0 / main_client
  const { coreData } = useContext(CoreContext);
  const { corporate: isCorporateMode } = coreData.booking_engine;

  const [postData, setPostData] = useState(null);
  const { data, status, executePost } = usePost(apiCheckout(storedCartId, isCorporateMode), postData);
  useReadCartMissingOrder(data);

  const { cleanOrderGotoCart } = useCleanStorage(false);

  const navigate = useNavigateBookingEngine();

  // This is to determine if we send the order.person_id
  const { isManufacturer } = useReadManufacturer();

  const loggedUser = useReadLoggedUser();

  const writeCheckout = paymentData => {
    const formattedData = formatCheckoutData(cartMainClientId, paymentData);

    // if the user gets disconnected when sending the checkout, this will allow us to fail
    if (loggedUser) {
      formattedData['checkout_source'] = CART_SOURCE_LOGGED_OFFICE;
    }

    // additional formatting methods that are inside this hook
    formatOrderPersonId(formattedData);
    formatCorporateMode(formattedData);

    setPostData(formattedData);
  };

  useUpdateEffect(() => {
    // This might be a cart reset, we just clean and redirect
    if (storedCartId == null) {
      cleanOrderGotoCart(t`Your cart has expired, please try again.`);
      return;
    }

    gtmPushBeginCheckout(storedCartId, totalPrice, currency);
    executePost();
  }, [postData]);

  useUpdateEffect(() => {
    if (status === REQUEST_STATUS.OK && data.success) {
      // Embedded payment: the order now exists and we confirm the card without leaving the page
      if (data.stripe && data.stripe.client_secret) {
        onStripeOrderCreated(data.stripe.client_secret);
        return;
      }

      if (data.payment_url !== null) {
        window.top.location.href = data.payment_url; // open in current tab (inside parent of iframe)
        // window.open(data.payment_url, '_blank'); // open in new tab
        // window.location.href = data.payment_url; // open in current tab (inside iframe)
        return;
      }
      navigate(ROUTE_PAGE_CHECKOUT_SUCCESS);
    }
  }, [status]);

  const formatOrderPersonId = formattedData => {
    const selector = formattedData['main_client'][CLIENT_SELECTOR_FIELDNAME];
    const selectorValue = selector ? selector.value : null;
    const isStaffSelector = selector ? selector.areChoicesStaffs : false;
    delete formattedData['main_client'][CLIENT_SELECTOR_FIELDNAME];

    // The order.person_id is only given in manufacturer mode
    if (!isManufacturer) return;

    // Manufacturer && admin => we have a staff selector so we send that value
    // Manufacturer NOT admin => we use the logged user
    formattedData['person_id'] = isStaffSelector ? selectorValue : loggedUser.id;
  };

  const formatCorporateMode = formattedData => {
    if (!isCorporateMode) return;
    delete formattedData['main_client'];
  };

  return { data, status, writeCheckout };
};

export default usePostCheckout;

const formatCheckoutData = (mainClientId, paymentData) => {
  const formattedData = {
    main_client: { ...paymentData },
  };

  formatPhoneNumber(formattedData['main_client']);
  formatPaymentMethod(paymentData, formattedData);
  formatPaymentLinkTemplate(paymentData, formattedData);
  formatSendBookingEmail(paymentData, formattedData);
  formatBusinessProvider(paymentData, formattedData);
  formatMergeOrder(paymentData, formattedData);
  formatDeposit(paymentData, formattedData);
  formatExtraData(paymentData, formattedData);
  trimObjectKeys(formattedData['main_client'], [FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME, EMAIL_FIELDNAME]);

  delete formattedData['main_client']['generalConditions'];
  formattedData['main_client']['id'] = mainClientId;

  return formattedData;
};

const formatPhoneNumber = mainClientData => {
  mainClientData[PHONE_FIELDNAME] = adaptPhoneForBackend(mainClientData[PHONE_FIELDNAME], mainClientData[PHONE_COUNTRYCODE_FIELDNAME]);
  delete mainClientData[PHONE_COUNTRYCODE_FIELDNAME];
};

const formatDeposit = (paymentData, formattedData) => {
  const deposit = paymentData[DEPOSIT_FIELDNAME];
  delete formattedData['main_client'][DEPOSIT_FIELDNAME];
  if (!deposit) return;

  formattedData[DEPOSIT_FIELDNAME] = deposit;
};

// only send this field when it's useful to prevent backend queries
const formatExtraData = (paymentData, formattedData) => {
  const allExtraData = paymentData[CHECKOUT_EXTRA_DATA_FIELDNAME];
  delete formattedData['main_client'][CHECKOUT_EXTRA_DATA_FIELDNAME];
  if (!allExtraData) return;

  let hasOneValueSet = false;
  for (const extraDataKey of Object.keys(allExtraData)) {
    if (typeof allExtraData[extraDataKey] === 'boolean') {
      if (allExtraData[extraDataKey] == null) continue;
    } else if (isStrNullOrEmpty(allExtraData[extraDataKey])) continue;

    hasOneValueSet = true;
    break;
  }

  if (hasOneValueSet) {
    formattedData[CHECKOUT_EXTRA_DATA_FIELDNAME] = allExtraData;
  }
};

const formatPaymentMethod = (paymentData, formattedData) => {
  const paymentMethod = paymentData[PAYMENT_METHOD_FIELDNAME];
  delete formattedData['main_client'][PAYMENT_METHOD_FIELDNAME];
  if (!paymentMethod) return;

  // If we selected the NO_PAYMENT_METHOD, we will send null to backend
  // So the backend wont create a payment for this order
  formattedData[PAYMENT_METHOD_FIELDNAME] = paymentMethod === NO_PAYMENT_METHOD ? null : paymentMethod;
};

const formatPaymentLinkTemplate = (paymentData, formattedData) => {
  const paymentLinkTemplate = paymentData[PAYMENTLINK_TEMPLATE_FIELDNAME];
  delete formattedData['main_client'][PAYMENTLINK_TEMPLATE_FIELDNAME];
  if (!paymentLinkTemplate) return;

  const paymentMethod = paymentData[PAYMENT_METHOD_FIELDNAME];
  if (paymentMethod !== PAYMENT_LINK_METHOD) return;

  formattedData[PAYMENTLINK_TEMPLATE_FIELDNAME] = paymentLinkTemplate;
};

const formatSendBookingEmail = (paymentData, formattedData) => {
  const sendBookingEmail = paymentData[SEND_BOOKING_MAIL_FIELDNAME];
  delete formattedData['main_client'][SEND_BOOKING_MAIL_FIELDNAME];
  if (!sendBookingEmail) return;

  // should be refactored properly with checkoutform.js
  if (
    paymentData[PAYMENT_METHOD_FIELDNAME] !== PAYMENT_LINK_METHOD &&
    paymentData[PAYMENT_METHOD_FIELDNAME] !== PAYMENT_TERMINAL_METHOD &&
    paymentData[PAYMENT_METHOD_FIELDNAME] !== ONLINE_PAYMENT_METHOD
  ) {
    formattedData[SEND_BOOKING_MAIL_FIELDNAME] = sendBookingEmail === i18n._(RADIO_YES);
  }
};

const formatBusinessProvider = (paymentData, formattedData) => {
  const businessProviderId = paymentData[BUSINESS_PROV_SELECTOR_FIELDNAME];
  delete formattedData['main_client'][BUSINESS_PROV_SELECTOR_FIELDNAME];
  if (!businessProviderId) return;

  formattedData[BUSINESS_PROV_SELECTOR_FIELDNAME] = businessProviderId;
};

const formatMergeOrder = (paymentData, formattedData) => {
  const mergeOrder = paymentData[MERGE_ORDER_FIELDNAME];
  delete formattedData['main_client'][MERGE_ORDER_FIELDNAME];
  if (!mergeOrder) return;

  formattedData[MERGE_ORDER_FIELDNAME] = mergeOrder;
};
